import React, { useState, useRef, useEffect, useId } from "react";
import { Controller } from "react-hook-form";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ErrorMessage,
  IconWrapper,
  InputWrapper,
  InputFieldWrapper,
  StyledInput,
} from "./styles";
import { IconButton } from "../../IconButton";
import { FormInputProps, MoneyInputFieldProps } from "./types";
import { formatCurrency } from "../../../utils/numbers";

const formatMoney = (value: number | string) => {
  if (value === "" || value === null || Number.isNaN(Number(value))) return "";
  return formatCurrency(Number(value));
};

const parseMoney = (value: string) => {
  if (!value) return "";
  return value.replaceAll(/[^0-9.,-]/g, "").replaceAll(",", ".");
};

export const FormMoneyInput = ({
  name,
  control,
  rules,
  disabled = false,
  placeholder = "",
  handleClear = false,
  autoFocus = false,
  variant = "default",
  id,
  required = false,
}: Omit<FormInputProps, "multiline" | "type">) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [autoFocus]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <MoneyInputField
          inputRef={inputRef}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          placeholder={placeholder}
          handleClear={handleClear}
          variant={variant}
          id={id}
          required={required}
        />
      )}
    />
  );
};

const MoneyInputField = ({
  inputRef,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  handleClear,
  variant,
  id,
  required,
}: MoneyInputFieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const [displayValue, setDisplayValue] = useState(
    value !== undefined && value !== null && value !== "" ? String(value) : "",
  );
  const [isFocused, setIsFocused] = useState(false);

  // Sync displayValue when value prop changes externally (e.g., via setValue)
  useEffect(() => {
    // Only update if not currently focused (to avoid interfering with user input)
    if (!isFocused) {
      const newDisplayValue =
        value !== undefined && value !== null && value !== ""
          ? formatMoney(value)
          : "";
      setDisplayValue(newDisplayValue);
    }
  }, [value, isFocused]);

  return (
    <InputWrapper>
      <InputFieldWrapper>
        <StyledInput
          ref={inputRef}
          id={inputId}
          type="text"
          $error={!!error}
          $variant={variant}
          $hasClear={Boolean(
            !disabled && handleClear && displayValue && displayValue.length > 0,
          )}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          value={displayValue}
          placeholder={placeholder}
          onChange={(e) => {
            const raw = parseMoney(e.target.value);
            setDisplayValue(raw);
            onChange(raw === "" ? "" : Number(raw));
          }}
          onBlur={() => {
            setIsFocused(false);
            if (displayValue !== "") {
              setDisplayValue(formatMoney(displayValue));
            }
          }}
          onFocus={() => {
            setIsFocused(true);
            // Show raw number when focusing so user can edit easily
            if (displayValue !== "") {
              setDisplayValue(String(value));
            }
          }}
        />
        {!disabled &&
          handleClear &&
          displayValue &&
          displayValue.length > 0 && (
            <IconWrapper>
              <IconButton
                size="small"
                onClick={() => {
                  setDisplayValue("");
                  onChange("");
                }}
                aria-label="Clear value"
              >
                <ClearIcon />
              </IconButton>
            </IconWrapper>
          )}
      </InputFieldWrapper>
      {error && (
        <ErrorMessage id={errorId} role="alert">
          {error.message}
        </ErrorMessage>
      )}
    </InputWrapper>
  );
};
